import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReportQuestion } from '../utils';
import { Answer } from '../../Review/utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import { LengthCounter } from '../../SectionLongText';
import { BottomBar } from '../../BottomBar';
import { useAnswers } from '../../../../BetaFeedback/AnswersProvider';
import { INTERIM_PROGRESS_NOTES_QUESTION_ID } from '../../../navUtils';
import { requestInit } from 'shared/auth/hooks/useRequestState';

interface ProgressNotesProps {
  report: Report;
  programName?: string;
  answers: Answer[];
  loadingAnswers: boolean;
  isInterimStage: boolean;
  questions: ReportQuestion[];
  reloadAnswers: () => unknown;
  onNextSection: () => unknown;
  onPreviousSection: () => unknown;
  beforeNext?: (description: string) => Promise<boolean>;
}

const StyledDiv = styled.div`
  text-align: center;
  margin-top: ${SPACERS.extraLarge};
  .progress-notes-heading,
  .progress-notes-subheading {
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
  .progress-notes-textarea {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.medium};
  }
  .progress-notes-counter {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.tiny};
  }
`;

const ProgressNotes: React.FC<ProgressNotesProps> = ({
  reloadAnswers,
  onNextSection,
  onPreviousSection,
  beforeNext,
}) => {
  const {
    answersById,
    updateAnswerRequest,
    updateAnswer,
    setUpdateAnswerRequest,
  } = useAnswers();
  const [text, setText] = useState<string>(
    answersById[INTERIM_PROGRESS_NOTES_QUESTION_ID]?.text || '',
  );

  useEffect(() => {
    if (updateAnswerRequest.type === 'REQUEST_SUCCESS') {
      reloadAnswers();
      onNextSection();
      setUpdateAnswerRequest(requestInit());
    }
  }, [updateAnswerRequest.type]);

  const handleNext = useCallback(async () => {
    if (beforeNext) {
      // this checks level1 feedback for donor name
      const proceed = await beforeNext(text);
      if (!proceed) return;
    }
    updateAnswer(
      {
        answer: text,
        questionId: INTERIM_PROGRESS_NOTES_QUESTION_ID,
        answerId: undefined,
      },
      'interim_report',
    );
  }, [text, onNextSection, updateAnswer, beforeNext]);

  return (
    <StyledDiv>
      <TrueImpactText variant="heading" className="progress-notes-heading">
        Summarize your program's progress and highlight any midpoint successes
        and/or challenges.
      </TrueImpactText>
      <TrueImpactText className="progress-notes-subheading">
        Note that this progress note will appear on your published report.
      </TrueImpactText>
      <TrueImpactTextfield
        isTextarea={true}
        height={193}
        className="progress-notes-textarea"
        placeholder="Add progress notes... (optional)"
        value={text}
        onChange={setText}
      />
      <div className="progress-notes-counter">
        <LengthCounter minLength={0} maxLength={900} length={text.length} />
      </div>
      <BottomBar
        disabled={false}
        hideBack={false}
        hideNext={false}
        handleNext={handleNext}
        handleBack={onPreviousSection}
        backTitle="Back"
        nextTitle="Save & Continue"
        isLoading={
          updateAnswerRequest.type === 'REQUEST_START' ||
          updateAnswerRequest.type === 'REQUEST_SUCCESS'
        }
      />
    </StyledDiv>
  );
};

export default ProgressNotes;
